// Small, pure helpers for the public venue projection (`PublicVenue`, spec/13 §13): where to find a
// venue's cover image, a one-line address, and the days of its opening hours in week order. No I/O,
// no formatting of money or time — that is the venue formatter's job.

import type { Address, OpeningHours, OpeningInterval, PublicVenue } from '@clavian/api-client';

/** The best cover image for a venue card, or `null` when it has none. */
export function coverUrl(venue: PublicVenue): string | null {
  return venue.photos.cover?.url ?? venue.photos.gallery[0]?.url ?? null;
}

/** A one-line address: locality and region, the human part of where a venue is. */
export function addressLine(address: Address | null): string | null {
  if (address === null) return null;
  return [address.locality, address.region].filter((part) => part && part.length > 0).join(', ') || null;
}

/** Distance in whole kilometres, when the directory computed one (a located search). */
export function distanceKm(venue: PublicVenue): number | null {
  return venue.distanceMeters === null ? null : Math.round(venue.distanceMeters / 100) / 10;
}

export const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type WeekDay = (typeof WEEK_DAYS)[number];

/** The opening intervals for one weekday. An empty list is "closed all day". */
export function intervalsOf(hours: OpeningHours, day: WeekDay): readonly OpeningInterval[] {
  return hours[day];
}

/** `"opens" === "closes"` is 24 hours (spec/13 §13). */
export function isAllDay(interval: OpeningInterval): boolean {
  return interval.opens === interval.closes;
}
